import { useEffect, useMemo, useState } from "react";
import { IShop } from "interfaces";
import cls from "./shopCard.module.scss";
import Link from "next/link";
import ShopLogo from "components/shopLogo/shopLogo";
import getImage from "utils/getImage";
import ShopBadges from "containers/shopBadges/shopBadges";
import useLocale from "hooks/useLocale";
import useShopWorkingSchedule from "hooks/useShopWorkingSchedule";
import VerifiedComponent from "components/verifiedComponent/verifiedComponent";
import { Rating } from "@mui/material";
import Image from "next/image";
import { shopHref } from "utils/shopHref";
import { useSettings } from "contexts/settings/settings.context";

// utils distanza
import { haversineKm, formatDistance } from "utils/distance";

type Props = {
  data: IShop;
};

/** Estrae lat/lon da vari formati possibili */
function extractCoords(input: any): { lat: number; lon: number } | null {
  if (!input) return null;

  // oggetto classico
  if (typeof input === "object") {
    const lat =
      input.latitude ??
      input.lat ??
      input.latitud ??
      (typeof input.location === "string"
        ? Number(String(input.location).match(/-?\d+(\.\d+)?/)?.[0])
        : undefined);
    const lon =
      input.longitude ??
      input.lon ??
      input.lng ??
      (typeof input.location === "string"
        ? Number(String(input.location).match(/-?\d+(\.\d+)?/g)?.[1])
        : undefined);

    if (Number.isFinite(lat) && Number.isFinite(lon)) {
      return { lat: Number(lat), lon: Number(lon) };
    }
  }

  // stringa "lat, lon" o "[lat, lon]"
  if (typeof input === "string") {
    const nums = input.match(/-?\d+(\.\d+)?/g);
    if (nums && nums.length >= 2) {
      const lat = Number(nums[0]);
      const lon = Number(nums[1]);
      if (Number.isFinite(lat) && Number.isFinite(lon)) {
        return { lat, lon };
      }
    }
  }

  return null;
}

/** Prova varie chiavi di localStorage per la posizione utente */
function getUserCoordsFromStorage(): { lat: number; lon: number } | null {
  const keys = [
    "location",
    "deliveryLocation",
    "selectedAddress",
    "delivery_address",
    "user_location",
    "map_address",
    "address",
  ];

  for (const k of keys) {
    try {
      const raw = localStorage.getItem(k);
      if (!raw) continue;

      // può essere stringa "lat,lon" oppure JSON
      const parsed = (() => {
        try {
          return JSON.parse(raw);
        } catch {
          return raw;
        }
      })();

      const fromParsed =
        extractCoords(parsed?.address?.location) ||
        extractCoords(parsed?.location) ||
        extractCoords(parsed);
      if (fromParsed) return fromParsed;
    } catch {
      // ignora
    }
  }
  return null;
}

export default function ShopCard({ data }: Props) {
  const { t } = useLocale();
  const { isShopClosed, workingSchedule } = useShopWorkingSchedule(data);

  // posizione utente dal SettingsContext (viene aggiornata da change address)
  const { location } = useSettings();

  const [distanceText, setDistanceText] = useState<string>("");

  // coordinate negozio (calcolate una volta)
  const shopCoords = useMemo(() => {
    return (
      extractCoords({
        latitude: (data as any)?.latitude,
        longitude: (data as any)?.longitude,
      }) ||
      extractCoords((data as any)?.location) ||
      extractCoords({
        latitude: (data as any)?.translation?.latitude,
        longitude: (data as any)?.translation?.longitude,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (!shopCoords) return;

    const setFromCoords = (lat: number, lon: number) => {
      const km = haversineKm(lat, lon, shopCoords.lat, shopCoords.lon);
      setDistanceText(formatDistance(km));
    };

    // 1) se nel context c'è la location (es. "41.45,15.55") → prioritaria
    const ctxCoords = extractCoords(location);
    if (ctxCoords) {
      setFromCoords(ctxCoords.lat, ctxCoords.lon);
      return;
    }

    // 2) fallback: localStorage
    const stored = typeof window !== "undefined" ? getUserCoordsFromStorage() : null;
    if (stored) {
      setFromCoords(stored.lat, stored.lon);
      return;
    }

    // 3) ultima spiaggia: geoloc browser
    if (typeof navigator !== "undefined" && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setFromCoords(pos.coords.latitude, pos.coords.longitude);
        },
        () => {
          // lascia vuoto se non disponibile
          setDistanceText("");
        },
        { enableHighAccuracy: false, maximumAge: 60_000, timeout: 3_000 }
      );
    } else {
      setDistanceText("");
    }
  }, [location, shopCoords]);

  return (
    <Link href={shopHref(data)} className={`${cls.wrapper}`}>
      <div className={cls.header}>
        <Image
          fill
          src={getImage(data.background_img)}
          alt={data.translation?.title}
          sizes="400px"
          quality={90}
          loading="lazy"
        />
      </div>

      <div className={cls.shopLogo}>
        <ShopLogo data={data} />
      </div>

      <div className={cls.body}>
        <ShopBadges data={data} />

        <div className={cls.titleContainer}>
          <h3 className={`${cls.title} ${!data?.verify && cls.maxWidth}`}>
            {data.translation?.title}
          </h3>
        {!!data?.verify && <VerifiedComponent />}
        </div>

        <p className={cls.text}>
          {!isShopClosed ? (
            <span>
              {workingSchedule?.from} - {workingSchedule?.to}
            </span>
          ) : (
            <span>{t("closed")}</span>
          )}
        </p>

        {distanceText && (
          <div
            style={{
              marginTop: 6,
              fontSize: 13,
              color: "var(--secondary-700)",
            }}
          >
            +{distanceText}
          </div>
        )}

        <div className={cls.rating}>
          <Rating
            value={data?.r_avg || 0}
            readOnly
            size="small"
            precision={0.5}
            name="ratingIcon"
            color="var(--orange)"
            className={cls.ratingIcon}
          />
          <p className={cls.ratingCount}>{data?.r_count || 0}</p>
        </div>
      </div>
    </Link>
  );
}
